// api.js - Every network call the front end makes. One fetch wrapper, one error
// shape, and named functions grouped by who calls them.
//
// The server returns { error, fields } on failure. ApiError carries both, so a
// form can show the message at the top and the field messages inline without any
// per-form error handling.
//
// LEGEND
//   1. BASE / request()   the wrapper
//   2. ApiError           the thrown shape
//   3. api.public         customer site
//   4. api.auth           sign in
//   5. api.orders         dashboard order work
//   6. api.admin          team, settings, log, inbox
//  6b. api.affiliates     the people who send us customers
//   7. api.shows          gold shows and who is coming

////////// 1. REQUEST //////////

// Same origin in development thanks to the Vite proxy; VITE_API_URL points at
// api.internationalgoldandsilver.com in production.
const BASE = (import.meta.env?.VITE_API_URL || "").replace(/\/$/, "");

export class ApiError extends Error {
  // `fields` renders against inputs. `body` is the whole answer, for a refusal
  // the caller has to do something about rather than print: the duplicate kit
  // request is answered with the order that already exists, and the form draws
  // a screen from it instead of showing a red line under a text box.
  constructor(message, { status, fields, body } = {}) {
    super(message);
    this.status = status;
    this.fields = fields || {};
    this.body = body || null;
  }
}

async function request(path, { method = "GET", body, query, signal, formData } = {}) {
  const url = new URL(`${BASE}${path}`, typeof window === "undefined" ? "http://localhost" : window.location.origin);
  for (const [key, value] of Object.entries(query || {})) {
    if (value !== undefined && value !== null && value !== "") url.searchParams.set(key, value);
  }

  let response;
  try {
    response = await fetch(url.toString(), {
      method,
      credentials: "include",
      signal,
      headers: formData ? undefined : { "Content-Type": "application/json" },
      body: formData || (body !== undefined ? JSON.stringify(body) : undefined),
    });
  } catch (error) {
    if (error.name === "AbortError") throw error;
    throw new ApiError("Could not reach the server. Check your connection and try again.", { status: 0 });
  }

  if (response.status === 204) return null;

  const text = await response.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { error: text }; }

  if (!response.ok) {
    throw new ApiError(data?.error || `Request failed (${response.status})`, {
      status: response.status,
      fields: data?.fields,
      body: data,
    });
  }
  return data;
}

const get = (path, query, signal) => request(path, { query, signal });
const post = (path, body) => request(path, { method: "POST", body });
const put = (path, body) => request(path, { method: "PUT", body });
const patch = (path, body) => request(path, { method: "PATCH", body });
const del = (path) => request(path, { method: "DELETE" });

////////// 3. CUSTOMER SITE //////////

// The affiliate's own page. Its own way in: no password anywhere, an emailed
// link or a texted code, and a signed pass the browser keeps. See
// server/src/routes/portal.js.
export const portalApi = {
  askForAccess: (input) => post("/api/portal/access", input),
  verifyAccess: (input) => post("/api/portal/access/verify", input),
  me: (token) => get("/api/portal/me", { token }),
};

export const publicApi = {
  settings: () => get("/api/public/settings"),
  spot: (signal) => get("/api/public/spot", undefined, signal),
  // The series behind every chart and ticker, ours and everybody else's.
  // { metals: "gold,silver", range: "1d", sparkline: true }
  chart: (query, signal) => get("/api/public/chart", query, signal),
  melt: (input) => post("/api/public/melt", input),
  requestKit: (input) => post("/api/public/requests", input),

  // Finishing an address somebody is typing. Both go through our server, which
  // holds the only key, and both carry the browser's session token so Google
  // bills one address rather than one keystroke. See ui/address.jsx.
  suggestAddress: (q, session, signal) => get("/api/public/address/suggest", { q, session }, signal),
  addressDetail: (placeId, session) => get(`/api/public/address/${encodeURIComponent(placeId)}`, { session }),
  // The one attribution a person actually chose. Optional, asked after the
  // request is in, and the tracking token is what authorises it.
  sayWhereTheyHeard: (token, input) => post(`/api/public/track/${token}/heard`, input),
  // Cover above the standard amount. Answered by a person, so the page that
  // calls this tells them to hold their package until it is.
  requestCoverage: (input) => post("/api/public/coverage", input),

  // An unfinished form. Fire and forget: the server answers 204 and the caller
  // must never wait on it or show an error, because this is not something the
  // person filling in the form asked for.
  saveDraft: (input) => {
    const body = JSON.stringify(input);
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const sent = navigator.sendBeacon(
        `${BASE}/api/public/requests/draft`,
        new Blob([body], { type: "application/json" })
      );
      if (sent) return Promise.resolve(null);
    }
    return post("/api/public/requests/draft", input).catch(() => null);
  },
  track: (token) => get(`/api/public/track/${token}`),
  respondToOffer: (token, input) => post(`/api/public/track/${token}/offer`, input),
  submitPayout: (token, input) => post(`/api/public/track/${token}/payout`, input),
  recover: (input) => post("/api/public/recover", input),
  verifyRecovery: (input) => post("/api/public/recover/verify", input),
  contact: (input) => post("/api/public/contact", input),

  // The affiliate application. The page it is posted from is not linked
  // anywhere: the address is sent to somebody after a conversation.
  applyAsAffiliate: (input) => post("/api/public/affiliates/apply", input),

  // Gold shows. The token in `mine` is the credential, the same idea as an order
  // tracking link, so nobody has to make an account to change their mind.
  shows: (signal) => get("/api/public/shows", undefined, signal),
  show: (slug, signal) => get(`/api/public/shows/${slug}`, undefined, signal),
  registerForShow: (slug, input) => post(`/api/public/shows/${slug}/register`, input),
  myShowSpot: (slug, token, signal) => get(`/api/public/shows/${slug}/me/${token}`, undefined, signal),
  cancelShowSpot: (slug, token) => post(`/api/public/shows/${slug}/me/${token}/cancel`),

  // The free price watch. No account, confirmed by email before anything sends.
  watchPrices: (input) => post("/api/public/alerts", input),
  // Mints the key that lets the embed builder drop our wordmark. Deliberately
  // not in the published embed documentation; see server/src/lib/tokens.js.
  createEmbed: (input) => post("/api/public/embeds", input),
};

////////// 4. AUTH //////////

export const authApi = {
  login: (input) => post("/api/auth/login", input),
  logout: () => post("/api/auth/logout"),
  me: () => get("/api/auth/me"),
  changePassword: (input) => post("/api/auth/password", input),
};

////////// 5. ORDERS //////////

export const ordersApi = {
  list: (query, signal) => get("/api/orders", query, signal),
  stats: () => get("/api/orders/stats"),
  create: (input) => post("/api/orders", input),
  get: (number) => get(`/api/orders/${number}`),
  update: (number, input) => patch(`/api/orders/${number}`, input),
  remove: (number) => del(`/api/orders/${number}`),

  setStatus: (number, input) => post(`/api/orders/${number}/status`, input),
  addNote: (number, input) => post(`/api/orders/${number}/note`, input),

  saveAppraisal: (number, lots) => put(`/api/orders/${number}/appraisal`, { lots }),
  priceAppraisal: (number, lots) => post(`/api/orders/${number}/appraisal/price`, { lots }),

  makeOffer: (number, input) => post(`/api/orders/${number}/offer`, input),
  recordResponse: (number, input) => post(`/api/orders/${number}/offer/response`, input),
  payout: (number, input) => post(`/api/orders/${number}/payout`, input),
  payoutDetails: (number) => get(`/api/orders/${number}/payout-details`),

  // Answering a higher value cover request. The customer is holding their
  // package until this happens.
  decideCoverage: (number, input) => post(`/api/orders/${number}/coverage`, input),

  // Both labels of a kit, for one order or for a morning's worth of them. The
  // response carries the printable pair back, so buying and printing is one trip.
  // `overrides` is keyed by leg and applies to every number in the batch. The
  // order page sends one number and whatever its dialog was changed to.
  buyKits: (numbers, notify = true, overrides) => post("/api/orders/kits", { numbers, notify, overrides }),
  kitSheet: (numbers, signal) => get("/api/orders/kits/sheet", { numbers: numbers.join(",") }, signal),
  // Written to each order's log once the paper has been through the printer.
  markKitsPrinted: (numbers) => post("/api/orders/kits/printed", { numbers }),

  buyLabel: (number, input) => post(`/api/orders/${number}/shipments`, input),
  attachTracking: (number, input) => post(`/api/orders/${number}/shipments/attach`, input),
  // For a leg with no bought label: somebody made the postage on stamps.com and
  // put it in the post. A tracking number is optional, because First Class Mail
  // on a flat does not come with one.
  markPosted: (number, leg, input = {}) => post(`/api/orders/${number}/shipments/${leg}/posted`, input),
  refreshTracking: (number, leg) => post(`/api/orders/${number}/shipments/${leg}/refresh`),
  voidLabel: (number, leg) => del(`/api/orders/${number}/shipments/${leg}`),
  // How much of our own cover a parcel is declared to be carrying. Changeable
  // until the carrier scans it, and every change is kept with a name on it.
  setInsurance: (number, leg, input) => patch(`/api/orders/${number}/shipments/${leg}/insurance`, input),

  uploadMedia: (number, files, { visibility = "internal", folder = "items", label } = {}) => {
    const form = new FormData();
    for (const file of files) form.append("files", file);
    form.append("visibility", visibility);
    form.append("folder", folder);
    if (label) form.append("label", label);
    return request(`/api/orders/${number}/media`, { method: "POST", formData: form });
  },
  updateMedia: (number, index, input) => patch(`/api/orders/${number}/media/${index}`, input),
  deleteMedia: (number, index) => del(`/api/orders/${number}/media/${index}`),

  messages: (number) => get(`/api/orders/${number}/messages`),
  sendMessage: (number, input) => post(`/api/orders/${number}/messages`, input),
  resend: (number, template) => post(`/api/orders/${number}/resend/${template}`),
};

////////// 6b. AFFILIATES //////////
// The people who send us customers. Codes are typed by hand, so every path here
// takes whatever was typed and lets the server normalise it.

export const affiliatesApi = {
  list: (query, signal) => get("/api/affiliates", query, signal),
  create: (input) => post("/api/affiliates", input),
  get: (code, query) => get(`/api/affiliates/${code}`, query),
  update: (code, input) => patch(`/api/affiliates/${code}`, input),
  remove: (code) => del(`/api/affiliates/${code}`),
  markPaid: (code, input) => post(`/api/affiliates/${code}/paid`, input),
  // The counter card for their display mailers. One card, printed four up on two
  // pages; how many copies is the browser's business.
  cards: (code, signal) => get(`/api/affiliates/${code}/cards`, undefined, signal),
  // Approving is what makes a code work at all, and it is the moment they are
  // told what theirs is.
  decide: (code, input) => post(`/api/affiliates/${code}/decision`, input),
  // Every keystroke of the affiliate box on the new order form.
  search: (q, signal) => get("/api/affiliates/lookup/search", { q }, signal),
};

////////// 6. ADMIN //////////

////////// 7. SHOWS //////////

export const showsApi = {
  list: (signal) => get("/api/shows", undefined, signal),
  get: (slug, signal) => get(`/api/shows/${slug}`, undefined, signal),
  create: (input) => post("/api/shows", input),
  update: (slug, input) => patch(`/api/shows/${slug}`, input),
  remove: (slug) => del(`/api/shows/${slug}`),

  addPhotos: (slug, files) => {
    const form = new FormData();
    for (const file of files) form.append("files", file);
    return request(`/api/shows/${slug}/gallery`, { method: "POST", formData: form });
  },
  removePhoto: (slug, index) => del(`/api/shows/${slug}/gallery/${index}`),

  setPerson: (slug, id, input) => patch(`/api/shows/${slug}/people/${id}`, input),
  message: (slug, input) => post(`/api/shows/${slug}/message`, input),
};

export const adminApi = {
  users: () => get("/api/admin/users"),
  createUser: (input) => post("/api/admin/users", input),
  updateUser: (id, input) => patch(`/api/admin/users/${id}`, input),
  deleteUser: (id) => del(`/api/admin/users/${id}`),
  saveSubscriptions: (input) => patch("/api/admin/me/subscriptions", input),

  settings: () => get("/api/admin/settings"),
  saveSettings: (input) => put("/api/admin/settings", input),

  leads: (query, signal) => get("/api/admin/leads", query, signal),
  updateLead: (id, input) => patch(`/api/admin/leads/${id}`, input),
  emailLead: (id) => post(`/api/admin/leads/${id}/email`),
  deleteLead: (id) => del(`/api/admin/leads/${id}`),

  events: (query, signal) => get("/api/admin/events", query, signal),
  messages: (query, signal) => get("/api/admin/messages", query, signal),
  markRead: (ids) => post("/api/admin/messages/read", { ids }),

  // The live push channel. An absolute URL because EventSource has no notion of
  // the fetch wrapper, and credentials because the session is a cookie on a
  // different origin in production.
  streamUrl: () => `${BASE}/api/admin/stream`,

  refreshSpot: () => post("/api/admin/spot/refresh"),
  inboxToken: () => post("/api/admin/inbox/token"),
  health: () => get("/api/admin/health"),

  // The Facebook lead sheet. `runImport` is the same function the five minute
  // job calls, so pressing the button and waiting for the job are the same
  // thing happening; it answers "already running" rather than starting a second.
  importStatus: () => get("/api/admin/leads/import"),
  runImport: () => post("/api/admin/leads/import"),

  // Signing into Google, so the sheet can be connected without a service account
  // key in an environment file. `connectGoogle` only asks for the URL; the
  // browser has to go there itself, and comes back to the callback route rather
  // than to the dashboard.
  connectGoogle: () => post("/api/admin/google/connect"),
  disconnectGoogle: () => post("/api/admin/google/disconnect"),

  // Push notifications to an installed dashboard. `endpoint` identifies this
  // browser on this device, so the answer says whether this one is registered
  // rather than whether the account is.
  push: (endpoint) => get("/api/admin/push", { endpoint }),
  subscribePush: (input) => post("/api/admin/push/subscribe", input),
  unsubscribePush: (endpoint) => post("/api/admin/push/unsubscribe", { endpoint }),
  testPush: () => post("/api/admin/push/test"),
};

export default { publicApi, authApi, ordersApi, showsApi, adminApi };
